import React, { useMemo } from 'react';
import { fmt, ReportRow } from './data';

interface CommentsRailProps {
  rows: ReportRow[];
  activeUid: string | null;
  onJump: (uid: string) => void;
}

export function CommentsRail({ rows, activeUid, onJump }: CommentsRailProps) {
  const cmts = useMemo(
    () => rows.filter((r) => r.comment).map((r) => ({ row: r, ...r.comment! })),
    [rows]
  );
  return (
    <aside className="rail" data-rail="comments">
      <div className="rail-head">
        <div className="t">Комментарии</div>
        <div className="c">{cmts.length}</div>
      </div>
      {cmts.length === 0 ? (
        <div className="cmt-empty">нет комментариев</div>
      ) : (
        cmts.map((c) => (
          <div
            key={c.row.uid}
            className={`cmt ${activeUid === c.row.uid ? 'active' : ''}`}
            onClick={() => onJump(c.row.uid)}
          >
            <div className="top">
              <div className="row-name">{c.row.name.trim()}</div>
              <div className="ts">{c.ts}</div>
            </div>
            <div className="body">{c.text}</div>
            <div className="ctx">
              <span className="v">{fmt(c.row.value)}</span>
              <span>·</span>
              <span>{(c.row.tableName || '').split('·')[0].trim()}</span>
            </div>
          </div>
        ))
      )}
    </aside>
  );
}

export function AuditRail({ row }: { row: ReportRow | undefined }) {
  if (!row) {
    return (
      <aside className="rail" data-rail="audit">
        <div className="rail-head">
          <div className="t">Audit trail</div>
          <div className="c">—</div>
        </div>
        <div className="audit-empty">
          Выберите строку, чтобы увидеть<br />
          источники и формулу.
        </div>
      </aside>
    );
  }
  const diff = row.master !== null ? row.value - (row.master as number) : null;
  const valClass = row.status === 'diff' ? 'diff' : (row.status === 'match' ? 'match' : '');

  return (
    <aside className="rail" data-rail="audit">
      <div className="rail-head">
        <div className="t">Audit trail</div>
        <div className="c">{row.uid}</div>
      </div>
      <div className="audit">
        <div className="row-title">{row.name.trim()}</div>
        <div className={`row-val ${valClass}`}>{fmt(row.value)}</div>
        <div className="row-sub">
          {row.master !== null ? (
            <>master {fmt(row.master)}{diff ? <> · Δ {fmt(diff)}</> : null}</>
          ) : (
            <>master не определён</>
          )}
        </div>

        {row.note && (
          <>
            <div className="h">Note</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              {row.note}
            </div>
          </>
        )}

        <div className="h">Источники</div>
        {(!row.sources || row.sources.length === 0) ? (
          <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>прямой источник не задан</div>
        ) : row.sources.map((s, i) => (
          <div key={i} className="pathline">
            <div className="lbl">
              <span style={{ color: 'var(--ink-2)' }}>{s.file}</span>
              <span style={{ color: 'var(--ink-4)' }}> · </span>
              <span>{s.cell}</span>
              <div style={{ fontFamily: 'var(--sans)', fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                {s.desc}
              </div>
            </div>
            <div className="val">{fmt(s.value)}</div>
          </div>
        ))}

        {row.formula && (
          <>
            <div className="h">Формула</div>
            <div className="formline">
              {row.formula.split(/(\+|−|\b\w+!\w+\b)/g).map((tok, i) => {
                if (tok === '+' || tok === '−' || tok === '-') return <span key={i} className="op"> {tok} </span>;
                if (/\w+!\w+/.test(tok)) return <span key={i} className="file">{tok}</span>;
                return <span key={i}>{tok}</span>;
              })}
            </div>
          </>
        )}

        <div className="h">Метаданные</div>
        <div className="pathline">
          <div className="lbl">cluster</div>
          <div className="val" style={{ fontSize: 11 }}>dd_1quart_2026</div>
        </div>
        <div className="pathline">
          <div className="lbl">config</div>
          <div className="val" style={{ fontSize: 11 }}>full_sgd_v3</div>
        </div>
        <div className="pathline">
          <div className="lbl">computed</div>
          <div className="val" style={{ fontSize: 11 }}>2026-04-29 11:42</div>
        </div>
      </div>
    </aside>
  );
}
